import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { ChevronDown, ChevronRight, X, Check, ScanLine } from 'lucide-react';
import { IconBuilding, IconFilterPlus, IconStack2 } from '@tabler/icons-react';
import { getApiBaseUrl } from '../../../utils/apiConfig';
import { BoardToolbarExpandSearch } from '../../board/toolbar/BoardToolbarExpandSearch';
import { BoardSearchSimplePickerPanel } from '../../board/board-search-filters/BoardSearchSimplePickerPanel';
import { EcClickUpFilterChip } from '../../experiencia-cliente/components/EcClickUpFilterChip';
import {
  buildEcFilterPopoverStyle,
  EC_FILTER_POPOVER_CLASS,
  useEcFilterPopoverLayout,
} from '../../experiencia-cliente/components/experienciaClienteFilterPopoverUtils';
import { DeqEquipTecnologiaFilterPanel } from './DeqEquipTecnologiaFilterPanel';
import '../../board/toolbar/BoardClickUpFilterChip.css';
import '../../board/board-search-filters/BoardSearchFilters.css';
import '../../board/toolbar/BoardToolbarSearchFilters.css';
import './DefeitosEquipCompatModal.css';

const API_BASE_URL = getApiBaseUrl();
const DEQ_FILTER_POPOVER_Z = 10070;

function withDeqPopoverStyle(layoutStyle) {
  if (!layoutStyle) return null;
  return { ...layoutStyle, zIndex: DEQ_FILTER_POPOVER_Z };
}

const AVATAR_PALETTES = [
  { bg: '#e0f2fe', text: '#0369a1', dot: '#38bdf8' },
  { bg: '#fef3c7', text: '#b45309', dot: '#fbbf24' },
  { bg: '#d1fae5', text: '#047857', dot: '#34d399' },
  { bg: '#ffe4e6', text: '#be123c', dot: '#fb7185' },
  { bg: '#ffedd5', text: '#c2410c', dot: '#fb923c' },
  { bg: '#dbeafe', text: '#1d4ed8', dot: '#60a5fa' },
  { bg: '#fee2e2', text: '#b91c1c', dot: '#f87171' },
  { bg: '#ede9fe', text: '#6d28d9', dot: '#a78bfa' },
];

function authHeaders() {
  const token = localStorage.getItem('authToken');
  return { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
}

function marcaInitials(nome) {
  const s = String(nome || '').trim();
  if (!s) return '?';
  const parts = s.split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return s.slice(0, 2).toUpperCase();
}

function paletteForMarca(name) {
  const s = String(name || '');
  let h = 0;
  for (let i = 0; i < s.length; i += 1) h = (h + s.charCodeAt(i)) % AVATAR_PALETTES.length;
  return AVATAR_PALETTES[h];
}

function toTitleCaseWords(text) {
  return String(text || '')
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => {
      if (w.length <= 4 && /^[A-Z0-9/&]+$/.test(w)) return w;
      return w.charAt(0).toUpperCase() + w.slice(1).toLowerCase();
    })
    .join(' ');
}

function techBadgeClass(tech, grupo) {
  const s = String(grupo || tech || '');
  if (/multifuncional/i.test(s)) return 'deq-tech-badge--multi';
  if (/jato/i.test(s)) return 'deq-tech-badge--jato';
  if (/laser/i.test(s)) return 'deq-tech-badge--laser';
  return 'deq-tech-badge--outro';
}

function buildTecGroupsFromApi(rows) {
  const map = new Map();
  for (const r of rows) {
    const grupo = String(r.grupo ?? '').trim() || '(Sem grupo)';
    const nome = String(r.nome ?? '').trim();
    if (!nome) continue;
    if (!map.has(grupo)) map.set(grupo, []);
    map.get(grupo).push(nome);
  }
  return [...map.entries()]
    .sort((a, b) => a[0].localeCompare(b[0], 'pt'))
    .map(([grupo, itens]) => ({
      grupo,
      itens: [...new Set(itens)].sort((a, b) => a.localeCompare(b, 'pt')),
    }));
}

function normalizeRow(r) {
  const id = String(r.cod_equip ?? '').trim();
  const tecnologia = String(r.nome_tecnologia ?? '').trim() || '(Sem tecnologia)';
  const grupo = String(r.nome_grupo ?? '').trim();
  return {
    id,
    modelo: String(r.nome_modelo ?? '').trim() || id,
    fabricante: String(r.nome_marca ?? '').trim() || '(Sem marca)',
    tecnologia,
    grupo,
    temScanner: /multifuncional/i.test(grupo || tecnologia),
  };
}

function Checkbox({ checked, onClick, title }) {
  return (
    <label
      className="deq-check-label"
      title={title}
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
    >
      <input
        type="checkbox"
        className="deq-check-native"
        checked={checked}
        onChange={() => onClick()}
        onClick={(e) => e.stopPropagation()}
        aria-label={title}
      />
      <span className="deq-check-box" aria-hidden />
    </label>
  );
}

function MarcaAvatar({ fabricante }) {
  const p = paletteForMarca(fabricante);
  return (
    <span className="deq-avatar" style={{ background: p.bg, color: p.text }}>
      {marcaInitials(fabricante)}
    </span>
  );
}

function TechBadge({ tech, grupo }) {
  return (
    <span className={`deq-tech-badge ${techBadgeClass(tech, grupo)}`} title={tech}>
      {toTitleCaseWords(tech)}
    </span>
  );
}

function DeqFiltersEmptyState() {
  return (
    <div className="deq-empty deq-empty--modern">
      <div className="deq-empty-placeholder">
        <IconFilterPlus size={48} stroke={1.25} className="deq-empty-icon" aria-hidden />
        <h3 className="deq-empty-modern-title">Aplique os filtros</h3>
        <p className="deq-empty-modern-desc">
          Escolha fabricante e/ou tecnologia, ou digite parte do modelo (mín. 2 caracteres) para listar
          equipamentos compatíveis.
        </p>
      </div>
    </div>
  );
}

/**
 * Modal widescreen: catálogo com checkboxes + painel de vinculados.
 */
export function DefeitosEquipCompatModal({ open, row, onClose, onLinkedCountChange, embedded = false }) {
  const codBug = row ? encodeURIComponent(String(row.TB01048_CODIGO).trim()) : '';
  const nomeDefeito = row ? String(row.TB01048_NOME || '').trim() : '';

  const [marcas, setMarcas] = useState([]);
  const [tecRows, setTecRows] = useState([]);
  const [allModels, setAllModels] = useState([]);
  const [fabricantes, setFabricantes] = useState([]);
  const [tecnologias, setTecnologias] = useState([]);
  const [busca, setBusca] = useState('');
  const [buscaDeb, setBuscaDeb] = useState('');
  const [linkedSet, setLinkedSet] = useState(() => new Set());
  const [linkedMeta, setLinkedMeta] = useState(() => new Map());
  const [collapsed, setCollapsed] = useState(() => new Set());
  const [linkedCollapsed, setLinkedCollapsed] = useState(() => new Set());
  const [loadingMeta, setLoadingMeta] = useState(false);
  const [loadingCat, setLoadingCat] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);
  const [toast, setToast] = useState(null);
  const [fabOpen, setFabOpen] = useState(false);
  const [tecOpen, setTecOpen] = useState(false);
  const [fabSearch, setFabSearch] = useState('');
  const [tecSearch, setTecSearch] = useState('');

  const fabBtnRef = useRef(null);
  const tecBtnRef = useRef(null);
  const initialLinkedRef = useRef(new Set());
  const onLinkedCountChangeRef = useRef(onLinkedCountChange);
  onLinkedCountChangeRef.current = onLinkedCountChange;

  const fabLayout = useEcFilterPopoverLayout(fabBtnRef, fabOpen, 236, 320);
  const tecLayout = useEcFilterPopoverLayout(tecBtnRef, tecOpen, 248, 360);
  const fabPopoverStyle = buildEcFilterPopoverStyle(fabLayout);
  const tecPopoverStyle = buildEcFilterPopoverStyle(tecLayout);

  const tecGroups = useMemo(() => buildTecGroupsFromApi(tecRows), [tecRows]);

  const fabItems = useMemo(
    () => marcas.map((m) => ({ value: m, label: m })),
    [marcas]
  );

  const filteredFabItems = useMemo(() => {
    const q = fabSearch.trim().toLowerCase();
    if (!q) return fabItems;
    return fabItems.filter((i) => String(i.label).toLowerCase().includes(q));
  }, [fabItems, fabSearch]);

  const catalogById = useMemo(() => {
    const m = new Map();
    for (const rowItem of allModels) {
      if (rowItem.id) m.set(rowItem.id, rowItem);
    }
    return m;
  }, [allModels]);

  const canLoadCatalog =
    fabricantes.length > 0 || tecnologias.length > 0 || buscaDeb.trim().length >= 2;

  const filtered = useMemo(() => {
    const q = buscaDeb.trim().toLowerCase();
    return allModels.filter((m) => q === '' || m.modelo.toLowerCase().includes(q));
  }, [allModels, buscaDeb]);

  const grouped = useMemo(() => {
    const map = new Map();
    for (const m of filtered) {
      if (!map.has(m.fabricante)) map.set(m.fabricante, []);
      map.get(m.fabricante).push(m);
    }
    return [...map.entries()].sort((a, b) => a[0].localeCompare(b[0], 'pt'));
  }, [filtered]);

  const linkedModels = useMemo(() => {
    const rows = [];
    for (const id of linkedSet) {
      const fromCat = catalogById.get(id);
      const meta = linkedMeta.get(id);
      if (fromCat) rows.push(fromCat);
      else if (meta) rows.push(meta);
    }
    return rows.sort((a, b) => a.modelo.localeCompare(b.modelo, 'pt'));
  }, [linkedSet, catalogById, linkedMeta]);

  const groupedLinked = useMemo(() => {
    const map = new Map();
    for (const m of linkedModels) {
      if (!map.has(m.fabricante)) map.set(m.fabricante, []);
      map.get(m.fabricante).push(m);
    }
    for (const models of map.values()) {
      models.sort((a, b) => a.modelo.localeCompare(b.modelo, 'pt'));
    }
    return [...map.entries()].sort((a, b) => a[0].localeCompare(b[0], 'pt'));
  }, [linkedModels]);

  const hasFilters = fabricantes.length > 0 || tecnologias.length > 0 || busca !== '';
  const allFilteredSelected = filtered.length > 0 && filtered.every((m) => linkedSet.has(m.id));

  useEffect(() => {
    const t = setTimeout(() => setBuscaDeb(busca.trim()), 380);
    return () => clearTimeout(t);
  }, [busca]);

  const fetchLinked = useCallback(async () => {
    if (!open || !codBug) return;
    const res = await fetch(`${API_BASE_URL}/api/config/defeitos-verve/${codBug}/equip-linked`, {
      headers: authHeaders(),
    });
    const linkedJ = await res.json();
    if (!linkedJ.success) {
      setErr(linkedJ.message || 'Erro ao carregar vínculos');
      setLinkedSet(new Set());
      setLinkedMeta(new Map());
      initialLinkedRef.current = new Set();
      return;
    }
    const list = Array.isArray(linkedJ.data) ? linkedJ.data : [];
    const ids = new Set();
    const meta = new Map();
    for (const r of list) {
      const n = normalizeRow(r);
      if (!n.id) continue;
      ids.add(n.id);
      meta.set(n.id, n);
    }
    setLinkedSet(ids);
    setLinkedMeta(meta);
    initialLinkedRef.current = new Set(ids);
    const cb = onLinkedCountChangeRef.current;
    const codPlain = decodeURIComponent(codBug);
    if (cb && codPlain) cb(codPlain, ids.size);
  }, [open, codBug]);

  const fetchTecs = useCallback(
    async (marcaFilter) => {
      if (!open || !codBug) return;
      const q = marcaFilter ? `?marca=${encodeURIComponent(marcaFilter)}` : '';
      try {
        const res = await fetch(
          `${API_BASE_URL}/api/config/defeitos-verve/${codBug}/equip-tecnologias${q}`,
          { headers: authHeaders() }
        );
        const j = await res.json();
        setTecRows(j.success && Array.isArray(j.data) ? j.data : []);
      } catch {
        setTecRows([]);
      }
    },
    [open, codBug]
  );

  const fetchCatalog = useCallback(async () => {
    if (!open || !codBug) return;
    const can =
      fabricantes.length > 0 || tecnologias.length > 0 || buscaDeb.trim().length >= 2;
    if (!can) {
      setAllModels([]);
      return;
    }
    setLoadingCat(true);
    setErr(null);
    try {
      const p = new URLSearchParams();
      fabricantes.forEach((m) => p.append('marca', m));
      tecnologias.forEach((t) => p.append('tecnologia', t));
      if (buscaDeb.trim().length >= 2) p.set('modelo', buscaDeb.trim());
      const res = await fetch(
        `${API_BASE_URL}/api/config/defeitos-verve/${codBug}/equip-catalog?${p}`,
        { headers: authHeaders() }
      );
      const catalogJ = await res.json();
      if (!catalogJ.success) {
        setErr(catalogJ.message || 'Erro ao carregar catálogo');
        setAllModels([]);
        return;
      }
      const seen = new Set();
      const normalized = [];
      for (const r of catalogJ.data || []) {
        const n = normalizeRow(r);
        if (!n.id || seen.has(n.id)) continue;
        seen.add(n.id);
        normalized.push(n);
      }
      setAllModels(normalized);
    } catch {
      setErr('Erro de conexão');
      setAllModels([]);
    } finally {
      setLoadingCat(false);
    }
  }, [open, codBug, fabricantes, tecnologias, buscaDeb]);

  const loadMeta = useCallback(async () => {
    if (!open || !codBug) return;
    setLoadingMeta(true);
    setErr(null);
    setFabricantes([]);
    setTecnologias([]);
    setBusca('');
    setBuscaDeb('');
    setAllModels([]);
    setCollapsed(new Set());
    setLinkedCollapsed(new Set());
    setFabOpen(false);
    setTecOpen(false);
    setFabSearch('');
    setTecSearch('');
    try {
      const headers = authHeaders();
      const [marcasRes] = await Promise.all([
        fetch(`${API_BASE_URL}/api/config/defeitos-verve/${codBug}/equip-marcas`, { headers }),
      ]);
      const marcasJ = await marcasRes.json();
      setMarcas(marcasJ.success && Array.isArray(marcasJ.data) ? marcasJ.data : []);
      await Promise.all([fetchTecs(''), fetchLinked()]);
    } catch {
      setErr('Erro de conexão');
      setMarcas([]);
      setTecRows([]);
      setLinkedSet(new Set());
      setLinkedMeta(new Map());
    } finally {
      setLoadingMeta(false);
    }
  }, [open, codBug, fetchTecs, fetchLinked]);

  useEffect(() => {
    if (!open) return;
    loadMeta();
  }, [open, loadMeta]);

  useEffect(() => {
    if (!open) return;
    fetchTecs(fabricantes.length === 1 ? fabricantes[0] : '');
  }, [open, fabricantes, fetchTecs]);

  useEffect(() => {
    if (!open) return;
    fetchCatalog();
  }, [open, fetchCatalog]);

  useEffect(() => {
    const valid = new Set(tecGroups.flatMap((g) => g.itens));
    setTecnologias((prev) => prev.filter((t) => valid.has(t)));
  }, [tecGroups]);

  useEffect(() => {
    if (!fabOpen) setFabSearch('');
  }, [fabOpen]);

  useEffect(() => {
    if (!tecOpen) setTecSearch('');
  }, [tecOpen]);

  useEffect(() => {
    if (!fabOpen) return undefined;
    const onDoc = (e) => {
      const pop = document.querySelector('[data-deq-fab-popover]');
      if (pop?.contains(e.target) || fabBtnRef.current?.contains(e.target)) return;
      setFabOpen(false);
    };
    document.addEventListener('mousedown', onDoc, true);
    return () => document.removeEventListener('mousedown', onDoc, true);
  }, [fabOpen]);

  useEffect(() => {
    if (!tecOpen) return undefined;
    const onDoc = (e) => {
      const pop = document.querySelector('[data-deq-tec-popover]');
      if (pop?.contains(e.target) || tecBtnRef.current?.contains(e.target)) return;
      setTecOpen(false);
    };
    document.addEventListener('mousedown', onDoc, true);
    return () => document.removeEventListener('mousedown', onDoc, true);
  }, [tecOpen]);

  function clearFilters() {
    setFabricantes([]);
    setTecnologias([]);
    setBusca('');
    setBuscaDeb('');
    setAllModels([]);
  }

  function toggleModel(model) {
    const id = model.id;
    setLinkedSet((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
    setLinkedMeta((prev) => {
      const next = new Map(prev);
      if (next.has(id)) next.delete(id);
      else next.set(id, model);
      return next;
    });
  }

  function toggleGroup(fab) {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(fab)) next.delete(fab);
      else next.add(fab);
      return next;
    });
  }

  function toggleLinkedGroup(fab) {
    setLinkedCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(fab)) next.delete(fab);
      else next.add(fab);
      return next;
    });
  }

  function toggleGroupSelection(models) {
    const allSelected = models.every((m) => linkedSet.has(m.id));
    setLinkedSet((prev) => {
      const next = new Set(prev);
      for (const m of models) {
        if (allSelected) next.delete(m.id);
        else next.add(m.id);
      }
      return next;
    });
    setLinkedMeta((prev) => {
      const next = new Map(prev);
      for (const m of models) {
        if (allSelected) next.delete(m.id);
        else next.set(m.id, m);
      }
      return next;
    });
  }

  function selectAllFiltered() {
    const allSelected = filtered.every((m) => linkedSet.has(m.id));
    setLinkedSet((prev) => {
      const next = new Set(prev);
      for (const m of filtered) {
        if (allSelected) next.delete(m.id);
        else next.add(m.id);
      }
      return next;
    });
    setLinkedMeta((prev) => {
      const next = new Map(prev);
      for (const m of filtered) {
        if (allSelected) next.delete(m.id);
        else next.set(m.id, m);
      }
      return next;
    });
  }

  function clearLinked() {
    setLinkedSet(new Set());
    setLinkedMeta(new Map());
  }

  function handleToggleFab(value) {
    setFabricantes((prev) =>
      prev.includes(value) ? prev.filter((m) => m !== value) : [...prev, value]
    );
  }

  function handleFabBulkToggle() {
    const visible = filteredFabItems.map((i) => i.value);
    if (!visible.length) return;
    const allSelected = visible.every((v) => fabricantes.includes(v));
    setFabricantes((prev) =>
      allSelected
        ? prev.filter((m) => !visible.includes(m))
        : Array.from(new Set([...prev, ...visible]))
    );
  }

  function handleToggleTec(nome) {
    setTecnologias((prev) =>
      prev.includes(nome) ? prev.filter((t) => t !== nome) : [...prev, nome]
    );
  }

  function handleToggleGrupo(grupo, itens) {
    const allSelected = itens.every((n) => tecnologias.includes(n));
    setTecnologias((prev) => {
      if (allSelected) return prev.filter((t) => !itens.includes(t));
      return Array.from(new Set([...prev, ...itens]));
    });
  }

  async function handleSave() {
    if (!codBug || saving) return;
    const initial = initialLinkedRef.current;
    const toAdd = [...linkedSet].filter((id) => !initial.has(id));
    const toRemove = [...initial].filter((id) => !linkedSet.has(id));
    if (!toAdd.length && !toRemove.length) {
      if (!embedded) onClose();
      return;
    }

    setSaving(true);
    setErr(null);
    try {
      if (toAdd.length) {
        const res = await fetch(`${API_BASE_URL}/api/config/defeitos-verve/${codBug}/equip-linked`, {
          method: 'POST',
          headers: authHeaders(),
          body: JSON.stringify({ codEquips: toAdd }),
        });
        const j = await res.json();
        if (!j.success) {
          setErr(j.message || 'Erro ao vincular equipamentos');
          return;
        }
      }
      if (toRemove.length) {
        const results = await Promise.all(
          toRemove.map((ce) =>
            fetch(`${API_BASE_URL}/api/config/defeitos-verve/${codBug}/equip-linked/${encodeURIComponent(ce)}`, {
              method: 'DELETE',
              headers: authHeaders(),
            }).then((r) => r.json())
          )
        );
        const failed = results.find((j) => !j.success);
        if (failed) {
          setErr(failed.message || 'Erro ao remover vínculos');
          await fetchLinked();
          return;
        }
      }

      initialLinkedRef.current = new Set(linkedSet);
      const cb = onLinkedCountChangeRef.current;
      const codPlain = decodeURIComponent(codBug);
      if (cb && codPlain) cb(codPlain, linkedSet.size);

      const n = linkedSet.size;
      setToast(`${n} modelo${n === 1 ? '' : 's'} vinculado${n === 1 ? '' : 's'} com sucesso`);
      setTimeout(() => setToast(null), 2600);
    } catch {
      setErr('Erro de conexão');
    } finally {
      setSaving(false);
    }
  }

  if (!open || !row) return null;

  const panel = (
      <div className={`defeitos-vv-equip-modal${embedded ? ' dv2-equip-embedded' : ''}`} onMouseDown={(e) => e.stopPropagation()}>
        <div className="deq-head">
          <div className="deq-head-text">
            <h2 className="deq-title">
              <span className="deq-title-main">
                Equipamentos compatíveis com o defeito &gt; {nomeDefeito || codBug}
              </span>
              <span className="deq-title-count">({linkedSet.size})</span>
            </h2>
            <p className="deq-subtitle">Selecione os modelos de impressora compatíveis com este defeito</p>
          </div>
          {embedded ? null : (
          <button type="button" className="deq-close" onClick={onClose} title="Fechar" aria-label="Fechar">
            <X size={15} strokeWidth={2} />
          </button>
          )}
        </div>

        {err ? <div className="deq-banner-err">{err}</div> : null}

        <div className="deq-filters">
          <EcClickUpFilterChip
            anchorRef={fabBtnRef}
            label="Fabricante"
            value={fabricantes.length > 0 ? String(fabricantes.length) : null}
            icon={IconBuilding}
            hasSelection={fabricantes.length > 0}
            isOpen={fabOpen}
            ariaLabel="Filtrar por fabricante"
            onClick={() => {
              setTecOpen(false);
              setFabOpen((o) => !o);
            }}
          />
          {fabOpen && fabPopoverStyle
            ? ReactDOM.createPortal(
                <div
                  data-deq-fab-popover
                  data-deq-filter-popover
                  className={`${EC_FILTER_POPOVER_CLASS} board-search-filters-popover--simple board-search-filters-popover--cidade deq-filter-popover`}
                  style={withDeqPopoverStyle(fabPopoverStyle)}
                  role="dialog"
                  onClick={(e) => e.stopPropagation()}
                  onMouseDown={(e) => e.stopPropagation()}
                >
                  <BoardSearchSimplePickerPanel
                    searchPlaceholder="Buscar fabricante..."
                    searchTerm={fabSearch}
                    onSearchChange={setFabSearch}
                    items={filteredFabItems}
                    getKey={(item) => item.value}
                    getLabel={(item) => item.label}
                    selectedKeys={fabricantes}
                    onToggle={(item) => handleToggleFab(item.value)}
                    emptyMessage="Nenhum fabricante"
                    listAriaLabel="Fabricantes"
                    bulkMenuEnabled
                    onBulkToggleAll={handleFabBulkToggle}
                  />
                </div>,
                document.body
              )
            : null}

          <EcClickUpFilterChip
            anchorRef={tecBtnRef}
            label="Tecnologia"
            value={tecnologias.length > 0 ? String(tecnologias.length) : null}
            icon={IconStack2}
            hasSelection={tecnologias.length > 0}
            isOpen={tecOpen}
            ariaLabel="Filtrar por tecnologia"
            onClick={() => {
              setFabOpen(false);
              setTecOpen((o) => !o);
            }}
          />
          {tecOpen && tecPopoverStyle
            ? ReactDOM.createPortal(
                <div
                  data-deq-tec-popover
                  data-deq-filter-popover
                  className={`${EC_FILTER_POPOVER_CLASS} board-search-filters-popover--simple board-search-filters-popover--cidade deq-filter-popover`}
                  style={withDeqPopoverStyle(tecPopoverStyle)}
                  role="dialog"
                  onClick={(e) => e.stopPropagation()}
                  onMouseDown={(e) => e.stopPropagation()}
                >
                  <DeqEquipTecnologiaFilterPanel
                    groups={tecGroups}
                    searchTerm={tecSearch}
                    onSearchChange={setTecSearch}
                    selectedTecs={tecnologias}
                    onToggleTec={handleToggleTec}
                    onToggleGrupo={handleToggleGrupo}
                    onSetTecs={setTecnologias}
                  />
                </div>,
                document.body
              )
            : null}

          <BoardToolbarExpandSearch
            value={busca}
            onChange={setBusca}
            placeholder="Buscar modelo..."
            ariaLabel="Buscar modelo"
          />

          {hasFilters ? (
            <button type="button" className="deq-clear-filters" onClick={clearFilters}>
              Limpar filtros
            </button>
          ) : null}

          {canLoadCatalog ? (
            <span className="deq-filter-count">
              {filtered.length} de {allModels.length} modelos
            </span>
          ) : null}
        </div>

        <div className="deq-main">
          <div className="deq-available">
            {canLoadCatalog && grouped.length > 0 ? (
              <div className="deq-select-all-bar">
                <span className="deq-lead-slot">
                  <Checkbox
                    checked={allFilteredSelected}
                    onClick={selectAllFiltered}
                    title="Selecionar todos os filtrados"
                  />
                </span>
                <span className="deq-select-all-label">
                  {allFilteredSelected ? 'Remover todos os filtrados' : 'Selecionar todos os filtrados'}
                </span>
              </div>
            ) : null}

            <div className="deq-scroll">
              {loadingMeta || loadingCat ? (
                <div className="deq-muted">Carregando…</div>
              ) : !canLoadCatalog ? (
                <DeqFiltersEmptyState />
              ) : grouped.length === 0 ? (
                <div className="deq-empty">
                  <div className="deq-empty-title">Nenhum modelo encontrado</div>
                  <div className="deq-empty-hint">Ajuste os filtros para ver mais resultados.</div>
                </div>
              ) : (
                grouped.map(([fab, models]) => {
                  const isCollapsed = collapsed.has(fab);
                  const selectedCount = models.filter((m) => linkedSet.has(m.id)).length;
                  const allFabSelected = models.every((m) => linkedSet.has(m.id));

                  return (
                    <div key={fab}>
                      <button type="button" className="deq-brand-head" onClick={() => toggleGroup(fab)}>
                        <span className="deq-lead-slot deq-brand-chevron" aria-hidden>
                          {isCollapsed ? (
                            <ChevronRight size={13} strokeWidth={2} />
                          ) : (
                            <ChevronDown size={13} strokeWidth={2} />
                          )}
                        </span>
                        <MarcaAvatar fabricante={fab} />
                        <span className="deq-brand-label">{fab}</span>
                        <span className="deq-brand-meta">
                          {models.length}
                          {selectedCount > 0 ? ` · ${selectedCount} vinc.` : ''}
                        </span>
                        <span
                          role="button"
                          tabIndex={0}
                          className="deq-brand-sel-all"
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleGroupSelection(models);
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              e.stopPropagation();
                              toggleGroupSelection(models);
                            }
                          }}
                        >
                          {allFabSelected ? 'rem. todos' : 'sel. todos'}
                        </span>
                      </button>

                      {!isCollapsed
                        ? models.map((m) => {
                            const isLinked = linkedSet.has(m.id);
                            return (
                              <div
                                key={m.id}
                                className={`deq-model-row${isLinked ? ' deq-model-row--linked' : ''}`}
                                onClick={() => toggleModel(m)}
                              >
                                <Checkbox checked={isLinked} onClick={() => toggleModel(m)} />
                                <span className="deq-model-name" title={m.modelo}>
                                  {m.modelo}
                                </span>
                                {m.temScanner ? (
                                  <ScanLine className="deq-scan-ic" size={13} strokeWidth={2} aria-hidden />
                                ) : null}
                                <TechBadge tech={m.tecnologia} grupo={m.grupo} />
                              </div>
                            );
                          })
                        : null}
                    </div>
                  );
                })
              )}
            </div>
          </div>

          <div className="deq-linked-panel">
            <div className="deq-linked-head">
              <span className="deq-linked-title">Vinculados · {linkedModels.length}</span>
              {linkedModels.length > 0 ? (
                <button type="button" className="deq-linked-clear" onClick={clearLinked}>
                  limpar
                </button>
              ) : null}
            </div>
            <div className="deq-scroll">
              {linkedModels.length === 0 ? (
                <div className="deq-empty">
                  <div className="deq-empty-title">Nenhum modelo vinculado</div>
                  <div className="deq-empty-hint">Selecione à esquerda para vincular.</div>
                </div>
              ) : (
                groupedLinked.map(([fab, models]) => {
                  const isCollapsed = linkedCollapsed.has(fab);
                  return (
                    <div key={`linked-${fab}`}>
                      <button
                        type="button"
                        className="deq-brand-head deq-brand-head--linked"
                        onClick={() => toggleLinkedGroup(fab)}
                      >
                        <span className="deq-lead-slot deq-brand-chevron" aria-hidden>
                          {isCollapsed ? (
                            <ChevronRight size={13} strokeWidth={2} />
                          ) : (
                            <ChevronDown size={13} strokeWidth={2} />
                          )}
                        </span>
                        <MarcaAvatar fabricante={fab} />
                        <span className="deq-brand-label">{fab}</span>
                        <span className="deq-brand-meta">{models.length}</span>
                      </button>
                      {!isCollapsed
                        ? models.map((m) => (
                            <div key={m.id} className="deq-linked-row" onClick={() => toggleModel(m)}>
                              <span className="deq-linked-name" title={m.modelo}>
                                {m.modelo}
                              </span>
                              <X className="deq-linked-remove" size={13} strokeWidth={2} aria-hidden />
                            </div>
                          ))
                        : null}
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>

        <div className="deq-foot user-form-modern-footer">
          <span className="deq-foot-count">
            <strong>{linkedSet.size}</strong> modelo{linkedSet.size === 1 ? '' : 's'} vinculado
            {linkedSet.size === 1 ? '' : 's'}
          </span>
          <div className="deq-foot-actions">
            <button
              type="button"
              className="user-form-link-btn user-form-footer-btn"
              onClick={onClose}
              disabled={saving}
              hidden={embedded}
            >
              Cancelar
            </button>
            <button
              type="button"
              className="user-form-profile-save-btn user-form-footer-btn"
              onClick={handleSave}
              disabled={saving || loadingMeta}
            >
              {saving ? 'Salvando…' : 'Salvar vínculos'}
            </button>
          </div>
        </div>
      </div>
  );

  const toastEl = toast ? (
    <div className="deq-toast">
      <Check className="deq-toast-ok" size={14} strokeWidth={2.5} aria-hidden />
      {toast}
    </div>
  ) : null;

  if (embedded) {
    return (
      <>
        {panel}
        {toastEl}
      </>
    );
  }

  return (
    <div
      className="defeitos-vv-modal-backdrop defeitos-vv-equip-backdrop"
      onMouseDown={(e) => e.target === e.currentTarget && onClose()}
    >
      {panel}
      {toastEl}
    </div>
  );
}
